import * as React from 'react'
import html2canvas from 'html2canvas'

import { render } from './render'
import { DrawingEngine } from '../../drawing/core/DrawingEngine'
import { interactionsList } from './interactionsList'

import {
  hitDetectionToCorner,
  hitDetectionToRect,
  hitDetectionToCircle,
  hitDetectionToLine,
  hitDetectionToCursor,
  resizeTextBoundary,
  resizeToMinBoundary,
} from '../../drawing/utils/'

import { deleteKey, backspaceKey, arrowLeftKey, arrowRightKey, keyPress } from './keyboardActions'


export const screenshotEditorController = (Component) => {
  return class Container extends React.Component {
    constructor(props) {
      super(props)

      this.editorRef = React.createRef()
      this.toolbarRef = React.createRef()
      this.toolbarDragRef = React.createRef()
      this.canvasRef = React.createRef()

      this.engine = new DrawingEngine()
      this.selectedItem = null
      this.backdropItem = null
      this.resizingItem = null

      this.textResizeOptions = {
        minWidth: 100,
        minHeight: 28,
        offset: 20,
        square: false,
      }

      this.cropResizeOptions = {
        minWidth: 100,
        minHeight: 100,
        offset: 20,
        square: false,
      }

      this.deleteButtonRadius = 23
      this.toolbarPaddingTop = 20
      this.hitDetectionToLineRadius = 10

      this.state = {
        activeType: 'backdrop',
        toolbarPosition: {
          left: 'calc(100% - 150px)',
          top: '10%',
        },
        status: null,
      }
    }

    componentDidMount() {
      const { engine, cropResizeOptions, textResizeOptions } = this
      const editor = (this.editor = this.editorRef.current)
      const canvas = (this.canvas = this.canvasRef.current)
      this.toolbar = this.toolbarRef.current
      this.toolbarDrag = this.toolbarDragRef.current

      this.initialResizeCanvas()

      engine.setInteractions(interactionsList.backdrop)

      engine.setMouseListeners(({ onMouseMove, onMouseDown, onMouseUp }) => {
        // mouse down action
        this.mouseDownHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()

          const area = engine.getArea()

          if (e.target === canvas) onMouseDown({ x: e.clientX - area.left, y: e.clientY - area.top })
          else if (e.target === this.toolbarDrag) this.setStatus('toolbarDrag')
        }

        // mouse move action
        this.mouseMoveHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()

          const area = engine.getArea()
          if (engine.getStatus() === 'toolbarDrag') this.toolbarMove(e)
          else onMouseMove({ x: e.clientX - area.left, y: e.clientY - area.top })
        }

        // mouse up action
        this.mouseUpHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()

          if (engine.getStatus() === 'toolbarDrag') this.setStatus(null)
          else onMouseUp()
        }

        // mouse leave action
        this.mouseLeaveHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()

          onMouseUp()
        }

        editor.addEventListener('mousemove', this.mouseMoveHandler)
        editor.addEventListener('mousedown', this.mouseDownHandler)
        editor.addEventListener('mouseup', this.mouseUpHandler)
        editor.addEventListener('mouseleave', this.mouseLeaveHandler)
      })

      engine.setRenderListener(itemViews => {
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        //clearing of canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        //drawing of opasity background
        if (itemViews.length > 1) {
          ctx.fillStyle = 'rgba(0,0,0, 0.4)'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
        }

        render(itemViews, ctx)
      })

      engine.registerInteractions([
        {
          type: 'click',
          name: 'removeFilledRectangle',
          hitDetection: absolutePoint => {
            if (!this.selectedItem || this.selectedItem.getStatus() === 'deleted') return false
            const boundary = this.selectedItem.getBoundary()
            return hitDetectionToCircle(
              { x: boundary.x + boundary.width, y: boundary.y },
              absolutePoint,
              this.deleteButtonRadius,
            )
          },
          onClick: _ => {
            if (!this.selectedItem) return

            this.selectedItem.remove()
            this.selectedItem = null
          },
        },

        {
          type: 'select',
          name: 'selectFilledRectangle',
          selectableEntities: ['filledRectangle'],
          hitDetection: hitDetectionToRect,
          bringToFrontAfterSelect: true,
          onSelect: item => {
            this.selectedItem = item
          },
        },

        {
          type: 'focus',
          name: 'focusFilledRectangle',
          draggableEntities: ['filledRectangle'],
          focusDetection: hitDetectionToRect,
        },

        {
          type: 'drag',
          name: 'dragFilledRectangle',
          draggableEntities: ['filledRectangle'],
          hitDetection: (itemDimensions, absolutePoint) => {
            if (!this.selectedItem || this.selectedItem.getStatus() === 'deleted') return false
            const boundary = this.selectedItem.getBoundary()

            if (itemDimensions.width !== boundary.width) return false
            if (itemDimensions.height !== boundary.height) return false

            return hitDetectionToRect(itemDimensions, absolutePoint)
          },
        },

        {
          type: 'resize',
          name: 'resizeFilledRectangle',
          resizeOptions: cropResizeOptions,
          resizableEntities: ['filledRectangle'],
          hitDetection: (itemDimensions, relativePoint) =>
            hitDetectionToCorner(itemDimensions, relativePoint, cropResizeOptions.offset),
        },

        {
          type: 'draw',
          name: 'drawFilledRectangle',
          entity: 'filledRectangle',
          getMountItem: _ => engine.getTopItem(),
          onStartDrawing: _ => {
            if (!this.selectedItem) return

            this.selectedItem.toggleSelection(false)
            this.selectedItem = null
          },
        },

        {
          type: 'drag',
          name: 'dragBackdropItem',
          draggableEntities: ['backdropItem'],
          hitDetection: (_, absolutePoint) => {
            if (!this.backdropItem || this.backdropItem.getStatus() === 'deleted') return false
            const boundary = this.backdropItem.getBoundary()
            return hitDetectionToRect(
              { width: boundary.width, height: boundary.height, angle: boundary.angle },
              absolutePoint,
            )
          },
        },

        {
          type: 'draw',
          name: 'drawBackdropItem',
          entity: 'backdropItem',
          getMountItem: _ => engine.getRootItem(),
          onStartDrawing: _ => {
            this.engine.clear('backdropItem')
            if (this.backdropItem) this.backdropItem = null
          },
          onEndDrawing: item => {
            this.backdropItem = item
          },
        },

        {
          type: 'draw',
          name: 'drawResizeItem',
          entity: 'resizeItem',
          getMountItem: _ => engine.getRootItem(),
          onStartDrawing: _ => {
            this.engine.clear('resizeItem')
            if (this.resizingItem) this.resizingItem = null
          },
          onEndDrawing: item => {
            this.resizingItem = item
            resizeToMinBoundary(item, 100, 100)
          },
        },

        {
          type: 'resize',
          name: 'resizeItem',
          resizeOptions: cropResizeOptions,
          resizableEntities: ['resizeItem'],
          hitDetection: (itemDimensions, relativePoint) =>
            hitDetectionToCorner(itemDimensions, relativePoint, cropResizeOptions.offset),
        },

        {
          type: 'drag',
          name: 'dragItem',
          draggableEntities: ['resizeItem'],
          hitDetection: hitDetectionToRect,
        },

        {
          type: 'click',
          name: 'removeLine',
          hitDetection: absolutePoint => {
            if (!this.selectedItem || this.selectedItem.getStatus() === 'deleted') return false
            const boundary = this.selectedItem.getBoundary()
            return hitDetectionToCircle(
              { x: boundary.x + boundary.width, y: boundary.y + boundary.height },
              absolutePoint,
              this.deleteButtonRadius,
            )
          },
          onClick: _ => {
            if (!this.selectedItem) return

            this.selectedItem.remove()
            this.selectedItem = null
          },
        },

        {
          type: 'select',
          name: 'selectLine',
          selectableEntities: ['line'],
          hitDetection: (itemDimensions, relativePoint) => {
            return hitDetectionToLine(itemDimensions, relativePoint, this.hitDetectionToLineRadius)
          },
          bringToFrontAfterSelect: true,
          onSelect: item => {
            this.selectedItem = item
          },
        },

        {
          type: 'focus',
          name: 'focusLine',
          draggableEntities: ['line'],
          focusDetection: (itemDimensions, relativePoint) => {
            return hitDetectionToLine(itemDimensions, relativePoint, this.hitDetectionToLineRadius)
          },
        },

        {
          type: 'drag',
          name: 'dragLine',
          draggableEntities: ['line', 'arrow'],
          hitDetection: (_, relativePoint) => {
            if (!this.selectedItem) return false
            const boundary = this.selectedItem.getBoundary()
            return hitDetectionToLine(
              { width: boundary.width, height: boundary.height, angle: boundary.angle },
              relativePoint,
              this.hitDetectionToLineRadius,
            )
          },
        },

        {
          type: 'draw',
          name: 'drawLine',
          entity: 'line',
          getMountItem: _ => engine.getTopItem(),
          onStartDrawing: _ => {
            if (this.selectedItem) {
              this.selectedItem.toggleSelection(false)
              this.selectedItem = null
            }
          },
        },

        {
          type: 'select',
          name: 'selectArrow',
          selectableEntities: ['arrow'],
          hitDetection: (itemDimensions, relativePoint) => {
            return hitDetectionToLine(itemDimensions, relativePoint, this.hitDetectionToLineRadius)
          },
          bringToFrontAfterSelect: true,
          onSelect: item => {
            this.selectedItem = item
          },
        },

        {
          type: 'focus',
          name: 'focusArrow',
          draggableEntities: ['arrow'],
          focusDetection: (itemDimensions, relativePoint) => {
            return hitDetectionToLine(itemDimensions, relativePoint, this.hitDetectionToLineRadius)
          },
        },

        {
          type: 'draw',
          name: 'drawArrow',
          entity: 'arrow',
          getMountItem: _ => engine.getTopItem(),
          onStartDrawing: _ => {
            if (!this.selectedItem) return

            this.selectedItem.toggleSelection(false)
            this.selectedItem = null
          },
        },

        {
          type: 'click',
          name: 'removeText',
          hitDetection: absolutePoint => {
            if (!this.selectedItem) return false
            const { selectedItem } = this

            if (selectedItem.getStatus() === 'deleted') {
              this.selectedItem = null
              return false
            }

            const boundary = selectedItem.getBoundary()
            return hitDetectionToCircle(
              { x: boundary.x + boundary.width, y: boundary.y },
              absolutePoint,
              this.deleteButtonRadius,
            )
          },
          onClick: _ => {
            if (!this.selectedItem) return

            this.selectedItem.remove()
            this.selectedItem = null
          },
        },

        {
          type: 'click',
          name: 'focusCursorText',
          hitDetection: absolutePoint => {
            if (!this.selectedItem) return false
            const { selectedItem } = this

            if (selectedItem.getStatus() === 'deleted') {
              this.selectedItem = null
              return false
            }
            return hitDetectionToCursor(selectedItem, this.canvas, absolutePoint)
          },
          onClick: _ => {},
        },

        {
          type: 'select',
          name: 'selectText',
          selectableEntities: ['text'],
          hitDetection: hitDetectionToRect,
          bringToFrontAfterSelect: true,
          onSelect: item => {
            this.selectedItem = item

            const ctx = this.canvas.getContext('2d')
            const { width, height } = resizeTextBoundary(item.getParams().text, ctx)
            this.textResizeOptions.minWidth = width > 100 ? width + 8 : 100
            this.textResizeOptions.minHeight = height > 28 ? height : 28
          },
        },

        {
          type: 'focus',
          name: 'focusText',
          draggableEntities: ['text'],
          focusDetection: hitDetectionToRect,
        },

        {
          type: 'drag',
          name: 'dragText',
          draggableEntities: ['text'],
          hitDetection: (itemDimensions, absolutePoint) => {
            if (!this.selectedItem) return false
            const { selectedItem } = this

            if (selectedItem.getStatus() === 'deleted') {
              this.selectedItem = null
              return false
            }

            const boundary = selectedItem.getBoundary()

            if (itemDimensions.width !== boundary.width) return false
            if (itemDimensions.height !== boundary.height) return false

            return hitDetectionToRect(itemDimensions, absolutePoint)
          },
        },

        {
          type: 'resize',
          name: 'resizeText',
          resizeOptions: textResizeOptions,
          resizableEntities: ['text'],
          hitDetection: (itemDimensions, relativePoint) =>
            hitDetectionToCorner(itemDimensions, relativePoint, textResizeOptions.offset),
        },

        {
          type: 'draw',
          name: 'drawText',
          entity: 'text',
          getMountItem: _ => engine.getTopItem(),
          onStartDrawing: _ => {
            if (this.selectedItem) {
              this.selectedItem.toggleSelection(false)
              this.selectedItem = null
            }
          },
          onEndDrawing: item => {
            this.selectedItem = item
            this.selectedItem.toggleSelection(true)
            resizeToMinBoundary(item, 100, 24)
          },
        },
      ])

      engine.setResizeOptions(cropResizeOptions)

      window.addEventListener('resize', this.resizeCanvas)
      window.addEventListener('keydown', this.keydown)
      window.addEventListener('keypress', this.keypress)
    }

    componentWillUnmount() {
      const { editor } = this

      editor.removeEventListener('mousemove', this.mouseMoveHandler)
      editor.removeEventListener('mousedown', this.mouseDownHandler)
      editor.removeEventListener('mouseup', this.mouseUpHandler)
      editor.removeEventListener('mouseleave', this.mouseLeaveHandler)

      window.removeEventListener('resize', this.resizeCanvas)
      window.removeEventListener('keydown', this.keydown)
      window.removeEventListener('keypress', this.keypress)
    }

    setStatus = (status) => {
      this.setState({ status })
      this.engine.setStatus(status)
    }

    changeType = (type) => {
      this.engine.setInteractions(interactionsList[type])
      this.setState({ activeType: type })
    }

    onSave = () => {
      if (this.selectedItem) {
        this.selectedItem.toggleSelection(false)
        this.selectedItem = null
        this.engine.reRender()
      }

      this.setState({ status: 'canvasCreation' })

      const documentBody = document.body
      const documentElement = document.documentElement || {}

      let x = window.scrollX || documentElement.scrollLeft || 0
      let y = window.scrollY || documentElement.scrollTop || 0

      const { editor } = this
      let width = editor.offsetWidth
      let height = editor.offsetHeight

      if (this.resizingItem && this.resizingItem.getStatus() !== 'deleted') {
        const boundary = this.resizingItem.getBoundary()
        y = boundary.y + window.pageYOffset + 3
        height = boundary.height - 6
        x = boundary.x + 3
        width = boundary.width - 6
      }

      setTimeout(() =>
        html2canvas(documentBody, {
          width,
          height,
          x,
          y,
          imageTimeout: 1000,
          ignoreElements: element => {
            return element.id === 'toolbar'
          },
        }).then(canvas => {
          this.props.onSubmit(canvas.toDataURL())
          this.setState({ status: null })
        }),
      )
    }

    initialResizeCanvas = () => {
      const { canvas, editor, engine } = this
      canvas.width = editor.offsetWidth
      canvas.height = editor.offsetHeight

      engine.setArea({ width: canvas.width, height: canvas.height })
      engine.reRender()
    }

    resizeCanvas = () => {
      const { canvas, editor, engine, toolbar } = this
      canvas.width = editor.offsetWidth
      canvas.height = editor.offsetHeight

      engine.setArea({ width: canvas.width, height: canvas.height })
      engine.reRender()

      let { offsetLeft, offsetTop } = toolbar
      let moveTooltip = false

      if (offsetLeft + toolbar.offsetWidth > canvas.width - 20) {
        offsetLeft = canvas.width - toolbar.offsetWidth - 20
        moveTooltip = true
      }

      if (offsetTop + toolbar.offsetHeight > canvas.height - 20) {
        offsetTop = canvas.height - toolbar.offsetHeight - 20
        moveTooltip = true
      }

      if (!moveTooltip) return

      this.setState({
        toolbarPosition: {
          left: offsetLeft + 'px',
          top: offsetTop + 'px',
        },
      })
    }

    //drag toolbar
    toolbarMove(e) {
      const { toolbar, editor, toolbarPaddingTop } = this

      let left = e.pageX - toolbar.offsetWidth / 2
      let top = e.pageY - toolbarPaddingTop - window.pageYOffset

      if (left < editor.offsetLeft + 20) left = editor.offsetLeft + 20

      if (top < editor.offsetTop + 20) top = editor.offsetTop + 20

      if (left + toolbar.offsetWidth > editor.offsetLeft + editor.offsetWidth - 20)
        left += editor.offsetLeft + editor.offsetWidth - (left + toolbar.offsetWidth) - 20

      if (top + toolbar.offsetHeight > editor.offsetTop + editor.offsetHeight - 20)
        top += editor.offsetTop + editor.offsetHeight - (top + toolbar.offsetHeight) - 20

      this.setState({
        toolbarPosition: {
          left: left + 'px',
          top: top + 'px',
        },
      })
    }

    keydown = (e) => {
      if (e.key.toLowerCase() === 'delete') return deleteKey.call(this, e)
      if (e.key.toLowerCase() === 'backspace') return backspaceKey.call(this, e)
      if (e.key.toLowerCase() === 'arrowleft') return arrowLeftKey.call(this, e)
      if (e.key.toLowerCase() === 'arrowright') return arrowRightKey.call(this, e)
    }

    keypress = (e) => {
      keyPress.call(this, e)
    }

    onUndo = () => {
      this.engine.undo()
    }

    render() {
      return (
        <Component
          activeType={this.state.activeType}
          status={this.state.status}
          toolbarPosition={this.state.toolbarPosition}
          changeType={this.changeType}
          editorRef={this.editorRef}
          canvasRef={this.canvasRef}
          toolbarRef={this.toolbarRef}
          toolbarDragRef={this.toolbarDragRef}
          onSave={this.onSave}
          onCancel={this.props.onCancel}
          onUndo={this.onUndo}
        />
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/ScreenshotEditor/screenshotEditorController.js